import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Fingerprint, MapPin, Camera, Clock, CheckCircle,
  XCircle, AlertCircle, Loader2, Server
} from "lucide-react";
import { isNative, getCurrentPosition, captureSelfie } from "@/lib/nativeBridge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PunchInOut = () => {
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  const [todayStatus, setTodayStatus] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [selfieVerified, setSelfieVerified] = useState(false);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [settings, setSettings] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [workingTimer, setWorkingTimer] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    fetchTodayStatus();
    fetchSettings();
    getLocation();
    fetchServerTime();
    
    // Update server time every second
    const serverTimeInterval = setInterval(fetchServerTime, 30000); // Sync every 30s
    
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      clearInterval(serverTimeInterval);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Real-time clock based on server time
  // Working hours timer - uses the punch time from database (already in IST)
  useEffect(() => {
    if (todayStatus?.record?.punch_in_time && !todayStatus?.record?.punch_out_time) {
      const updateWorkingTime = () => {
        // Parse punch time (stored in IST without timezone suffix)
        const punchInStr = todayStatus.record.punch_in_time;
        let punchIn;
        try {
          // Handle format: "2026-02-16T14:30:00" (IST)
          punchIn = new Date(punchInStr);
          if (isNaN(punchIn.getTime())) {
            // Fallback: try parsing with timezone
            punchIn = new Date(punchInStr + "+05:30");
          }
        } catch {
          punchIn = new Date();
        }
        
        const now = new Date();
        const diff = now - punchIn;
        
        // If diff is negative (clock mismatch), show 0
        if (diff < 0) {
          setWorkingTimer("00:00:00");
          return;
        }
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setWorkingTimer(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      };
      
      updateWorkingTime();
      timerIntervalRef.current = setInterval(updateWorkingTime, 1000);
      
      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    } else {
      setWorkingTimer(null);
    }
  }, [todayStatus?.record?.punch_in_time, todayStatus?.record?.punch_out_time]);

  const fetchServerTime = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/server-time`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        // Store server time with fetch timestamp for sync
        setServerTime({
          ...data,
          fetchedAt: Date.now()
        });
      }
    } catch (error) {
      console.error("Error fetching server time:", error);
    }
  };

  // Live clock that syncs with server time
  const [liveTime, setLiveTime] = useState({ hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    if (serverTime?.hours !== undefined) {
      // Calculate time offset from when we fetched
      const updateLiveTime = () => {
        const elapsedMs = Date.now() - (serverTime.fetchedAt || Date.now());
        const elapsedSeconds = Math.floor(elapsedMs / 1000);
        
        let totalSeconds = serverTime.hours * 3600 + serverTime.minutes * 60 + serverTime.seconds + elapsedSeconds;
        
        // Handle day overflow
        totalSeconds = totalSeconds % 86400;
        
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        
        setLiveTime({ hours, minutes, seconds });
      };
      
      updateLiveTime();
      const interval = setInterval(updateLiveTime, 1000);
      return () => clearInterval(interval);
    }
  }, [serverTime]);

  const formatLiveTime = () => {
    const h = liveTime.hours;
    const m = liveTime.minutes;
    const s = liveTime.seconds;
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    return `${displayHour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')} ${period}`;
  };

  const fetchTodayStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/today`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setTodayStatus(data);
      }
    } catch (error) {
      console.error("Error fetching today status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/settings`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const getLocation = async () => {
    try {
      const pos = await getCurrentPosition({ timeout: 12000 });
      setLocation({ latitude: pos.latitude, longitude: pos.longitude });
      setLocationError(null);
    } catch (err) {
      setLocationError(err.message || "Unable to get location");
    }
  };

  const startCamera = async () => {
    // On Android, route through the native camera. The selfie is captured
    // in a single OS-level flow — no <video> preview, no media stream.
    if (isNative) {
      try {
        const { dataUrl } = await captureSelfie();
        if (dataUrl) setSelfiePreview(dataUrl);
        setSelfieVerified(true);
        toast.success("Selfie captured");
      } catch (error) {
        toast.error(error.message || "Unable to capture selfie");
      }
      return;
    }
    // Web fallback — same getUserMedia flow as before.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setShowCamera(true);
    } catch (error) {
      toast.error("Unable to access camera. Please check permissions.");
    }
  };

  const takeSelfie = () => {
    setSelfieVerified(true);
    setShowCamera(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    toast.success("Selfie verified!");
  };

  const retakeSelfie = () => {
    setSelfieVerified(false);
    setSelfiePreview(null);
    startCamera();
  };

  const handlePunchIn = async () => {
    if (settings?.require_selfie && !selfieVerified) {
      toast.error("Please verify your selfie first");
      return;
    }

    if (settings?.require_gps && !location) {
      toast.error("Location is required. Please enable GPS.");
      return;
    }

    setPunching(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/punch-in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          selfie_verified: selfieVerified,
          latitude: location?.latitude,
          longitude: location?.longitude
        })
      });

      const data = await response.json();

      if (response.ok) {
        const serverPunchTime = data.punch_in_time_display || formatTime(data.punch_in_time);
        toast.success(`Punch In recorded at ${serverPunchTime} (Server Time IST)`);
        if (data.is_late) {
          toast.warning(`You are ${data.late_minutes} minutes late`);
        }
        fetchTodayStatus();
        fetchServerTime();
      } else if (response.status === 429) {
        toast.error("Please wait a few seconds before trying again");
      } else {
        toast.error(data.detail || "Failed to punch in");
      }
    } catch (error) {
      toast.error("Error recording punch in");
    } finally {
      setPunching(false);
    }
  };

  const handlePunchOut = async () => {
    setPunching(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/punch-out`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          latitude: location?.latitude,
          longitude: location?.longitude
        })
      });

      const data = await response.json();

      if (response.ok) {
        const serverPunchTime = data.punch_out_time_display || formatTime(data.punch_out_time);
        toast.success(`Punch Out recorded at ${serverPunchTime} (Server Time IST)`);
        toast.info(`Total hours: ${data.working_hours}h`);
        if (data.overtime_hours > 0) {
          toast.success(`Overtime: ${data.overtime_hours}h`);
        }
        fetchTodayStatus();
        fetchServerTime();
      } else if (response.status === 429) {
        toast.error("Please wait a few seconds before trying again");
      } else {
        toast.error(data.detail || "Failed to punch out");
      }
    } catch (error) {
      toast.error("Error recording punch out");
    } finally {
      setPunching(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "--:--";
    return new Date(isoString).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const canPunchIn = !todayStatus?.punched_in;
  const canPunchOut = todayStatus?.punched_in && !todayStatus?.punched_out;
  const dayComplete = todayStatus?.punched_out;

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="punch-page">
      {/* Header with Server Time */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Fingerprint className="w-8 h-8 text-emerald-600" />
            Punch In / Out
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {serverTime?.server_date_full || new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric"
            })}
          </p>
        </div>
        
        {/* Live Server Time Display */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl shadow-lg">
          <div className="flex items-center gap-3">
            <Server className="w-6 h-6" />
            <div>
              <p className="text-xs opacity-90 font-medium">Server Time (IST)</p>
              <p className="text-2xl font-mono font-bold tracking-wider" data-testid="server-clock">
                {formatLiveTime()}
              </p>
            </div>
          </div>
          <div className="mt-1 text-xs opacity-80 text-center">
            Date: {serverTime?.server_date_display || '--'} | Timezone: Asia/Kolkata
          </div>
        </div>
      </div>

      {/* Current Status Card */}
      <Card className={`border-2 ${
        dayComplete 
          ? "border-green-500 bg-green-50 dark:bg-green-900/20" 
          : canPunchOut 
            ? "border-amber-500 bg-amber-50 dark:bg-amber-900/20"
            : "border-slate-200 dark:border-slate-700"
      }`}>
        <CardContent className="p-6">
          <div className="text-center">
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
              dayComplete 
                ? "bg-green-100 dark:bg-green-900/30" 
                : canPunchOut 
                  ? "bg-amber-100 dark:bg-amber-900/30"
                  : "bg-slate-100 dark:bg-slate-800"
            }`}>
              {dayComplete ? (
                <CheckCircle className="w-10 h-10 text-green-600" />
              ) : canPunchOut ? (
                <Clock className="w-10 h-10 text-amber-600 animate-pulse" />
              ) : (
                <XCircle className="w-10 h-10 text-slate-400" />
              )}
            </div>
            
            <h2 className="text-xl font-bold text-slate-800 dark:text-white">
              {dayComplete 
                ? "Day Complete" 
                : canPunchOut 
                  ? "Currently Working" 
                  : "Not Punched In"}
            </h2>

            {/* Running Working Hours Timer */}
            {workingTimer && canPunchOut && (
              <div className="mt-3 inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/30 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="font-mono text-lg font-bold text-blue-700 dark:text-blue-300" data-testid="working-timer">
                  {workingTimer}
                </span>
                <span className="text-xs text-blue-600">working</span>
              </div>
            )}

            {todayStatus?.record && (
              <div className="mt-4 flex justify-center gap-8">
                <div>
                  <p className="text-sm text-slate-500">Punch In</p>
                  <p className="text-lg font-semibold text-emerald-600">
                    {todayStatus.record.punch_in_time_display || todayStatus.record.punch_in_time_local || formatTime(todayStatus.record.punch_in_time)}
                  </p>
                </div>
                {todayStatus.record.punch_out_time && (
                  <div>
                    <p className="text-sm text-slate-500">Punch Out</p>
                    <p className="text-lg font-semibold text-red-600">
                      {todayStatus.record.punch_out_time_display || todayStatus.record.punch_out_time_local || formatTime(todayStatus.record.punch_out_time)}
                    </p>
                  </div>
                )}
                {todayStatus.record.working_hours > 0 && (
                  <div>
                    <p className="text-sm text-slate-500">Hours</p>
                    <p className="text-lg font-semibold text-blue-600">
                      {todayStatus.record.working_hours.toFixed(1)}h
                    </p>
                  </div>
                )}
              </div>
            )}

            {todayStatus?.record?.is_late && (
              <div className="mt-3 inline-flex items-center gap-1 px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-sm">
                <AlertCircle className="w-4 h-4" />
                Late by {todayStatus.record.late_minutes} minutes
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Verification Section */}
      {!dayComplete && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* GPS Location */}
          <Card className="border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="w-5 h-5 text-emerald-600" />
                GPS Location
                {settings?.require_gps && <span className="text-red-500 text-sm">*</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {location ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm">Location captured</span>
                </div>
              ) : locationError ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-red-600">
                    <XCircle className="w-5 h-5" />
                    <span className="text-sm">{locationError}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={getLocation}>
                    Retry
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Fetching location...</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Selfie Verification */}
          <Card className="border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Camera className="w-5 h-5 text-emerald-600" />
                Selfie Verification
                {settings?.require_selfie && <span className="text-red-500 text-sm">*</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selfieVerified ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm">Selfie verified</span>
                  </div>
                  {selfiePreview && (
                    <div className="flex items-start gap-3">
                      <img
                        src={selfiePreview}
                        alt="Selfie preview"
                        className="w-20 h-20 object-cover rounded-lg border border-slate-200"
                      />
                      <Button size="sm" variant="outline" onClick={retakeSelfie}>
                        Retake
                      </Button>
                    </div>
                  )}
                </div>
              ) : showCamera ? (
                <div className="space-y-3">
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline 
                    className="w-full rounded-lg"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={takeSelfie} className="flex-1">
                      Capture
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => {
                        setShowCamera(false);
                        if (streamRef.current) {
                          streamRef.current.getTracks().forEach(track => track.stop());
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={startCamera}>
                  <Camera className="w-4 h-4 mr-2" />
                  Take Selfie
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Punch Buttons */}
      {!dayComplete && (
        <div className="flex flex-col sm:flex-row gap-4">
          {canPunchIn && (
            <Button
              size="lg"
              className="flex-1 h-16 text-lg bg-emerald-600 hover:bg-emerald-700"
              onClick={handlePunchIn}
              disabled={punching || (settings?.require_gps && !location) || (settings?.require_selfie && !selfieVerified)}
              data-testid="punch-in-btn"
            >
              {punching ? (
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
              ) : (
                <Fingerprint className="w-6 h-6 mr-2" />
              )}
              Punch In
            </Button>
          )}

          {canPunchOut && (
            <Button
              size="lg"
              variant="destructive"
              className="flex-1 h-16 text-lg"
              onClick={handlePunchOut}
              disabled={punching}
              data-testid="punch-out-btn"
            >
              {punching ? (
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
              ) : (
                <Fingerprint className="w-6 h-6 mr-2" />
              )}
              Punch Out
            </Button>
          )}
        </div>
      )}

      {/* Office Hours Info */}
      {settings && (
        <Card className="border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Office Hours: {settings.office_start_time} - {settings.office_end_time}</span>
              </div>
              <div>Grace Period: {settings.late_grace_minutes} mins</div>
              <div>Working Hours: {settings.working_hours_per_day}h/day</div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PunchInOut;
